import { useState, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import StepLayout from './components/StepLayout';
import StepPeople from './components/StepPeople';
import StepStyle from './components/StepStyle';
import StepStage from './components/StepStage';
import StepTaste from './components/StepTaste';
import StepAvoid from './components/StepAvoid';
import StepShopStyle from './components/StepShopStyle';
import StepShopChoice from './components/StepShopChoice';
import StepSchedule from './components/StepSchedule';
import ResultScreen from './components/ResultScreen';
const STEPS = [
  {
    key: 'people',
    question: '예복 준비는 어떤 분위기로 진행될 예정인가요?',
    description: '함께 방문하는 분들에 따라 추천 방식이 달라져요.',
    component: StepPeople,
    canNext: (v: Record<string, number>) => Object.values(v).some((n) => n > 0),
  },
  {
    key: 'style',
    question: '예복 준비에서 더 가까운 쪽은?',
    description: '당신에게 가장 잘 맞는 예복 준비 방식을 선택해주세요.',
    component: StepStyle,
    canNext: (v: string | null) => v !== null,
  },
  {
    key: 'stage',
    question: '현재 결혼 준비는 어느 단계인가요?',
    description: '현재 상황에 맞춰 가장 적절한 예복 가이드를 추천해드릴게요.',
    component: StepStage,
    canNext: (v: string | null) => v !== null,
  },
  {
    key: 'taste',
    question: '예복샵에서 가장 끌리는 순간은?',
    description: '당신의 예복 취향을 분석하기 위한 질문이에요.',
    component: StepTaste,
    canNext: (v: string | null) => v !== null,
  },
  {
    key: 'avoid',
    question: '예복 준비에서 가장 피하고 싶은 건?',
    description: '피하고 싶은 상황을 알면 더 정확한 추천이 가능해요.',
    component: StepAvoid,
    canNext: (v: string | null) => v !== null,
  },
  {
    key: 'shopStyle',
    question: '어떤 방식으로 예복샵을 고르고 싶으세요?',
    description: '추천 방식을 맞춤 조정할게요.',
    component: StepShopStyle,
    canNext: (v: string | null) => v !== null,
  },
  {
    key: 'shop',
    question: '이미 생각해둔 예복샵이 있으신가요?',
    description: '관심 샵이 있다면 검색하거나 추천 샵 중 선택하세요.',
    component: StepShopChoice,
    canNext: (v: { hasShop: boolean; shopName: string | null }) =>
      v.hasShop !== undefined && (v.hasShop === false || v.shopName !== null),
  },
  {
    key: 'schedule',
    question: '방문 상담 희망 일정을 알려주세요',
    description: '예복샵 방문을 희망하시는 날짜를 알려주세요. 업체와 조율에 참고할게요.',
    component: StepSchedule,
    canNext: (v: string | null) => v !== null && v !== '',
  },
];

export default function YangbokPage() {
  const [currentStep, setCurrentStep] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [direction, setDirection] = useState(1);

  // State for each step
  const [people, setPeople] = useState<Record<string, number>>({});
  const [style, setStyle] = useState<string | null>(null);
  const [stage, setStage] = useState<string | null>(null);
  const [taste, setTaste] = useState<string | null>(null);
  const [avoid, setAvoid] = useState<string | null>(null);
  const [shopStyle, setShopStyle] = useState<string | null>(null);
  const [shop, setShop] = useState<{ hasShop: boolean; shopName: string | null }>({ hasShop: false, shopName: null });
  const [schedule, setSchedule] = useState<string | null>(null);

  const values: Record<string, unknown> = {
    people,
    style,
    stage,
    taste,
    avoid,
    shopStyle,
    shop,
    schedule,
  };

  const step = STEPS[currentStep];
  const StepComponent = step.component;
  const canNext = step.canNext(values[step.key] as never);

  const goNext = useCallback(() => {
    if (!canNext) return;
    if (currentStep < STEPS.length - 1) {
      setDirection(1);
      setCurrentStep((s) => s + 1);
    } else {
      setShowResult(true);
    }
  }, [canNext, currentStep]);

  const goBack = useCallback(() => {
    if (currentStep > 0) {
      setDirection(-1);
      setCurrentStep((s) => s - 1);
    }
  }, [currentStep]);

  const reset = useCallback(() => {
    setCurrentStep(0);
    setShowResult(false);
    setPeople({});
    setStyle(null);
    setStage(null);
    setTaste(null);
    setAvoid(null);
    setShopStyle(null);
    setShop({ hasShop: false, shopName: null });
    setSchedule(null);
    setDirection(1);
  }, []);

  const variantProps = {
    initial: { opacity: 0, x: direction * 40 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: direction * -40 },
    transition: { duration: 0.35, ease: 'easeOut' },
  };

  if (showResult) {
    return (
      <ResultScreen
        people={people}
        style={style}
        stage={stage}
        taste={taste}
        avoid={avoid}
        shopStyle={shopStyle}
        shopName={shop.shopName}
        hasShop={shop.hasShop}
        schedule={schedule}
        onRetry={reset}
      />
    );
  }

  return (
    <StepLayout
      step={currentStep + 1}
      totalSteps={STEPS.length}
      question={step.question}
      description={step.description}
      onBack={goBack}
      onNext={goNext}
      canGoNext={canNext}
    >
      <AnimatePresence mode="wait" custom={direction}>
        <motion.div key={step.key} {...variantProps}>
          {step.key === 'people' && (
            <StepPeople value={people} onChange={setPeople} />
          )}
          {step.key === 'style' && (
            <StepStyle value={style} onChange={setStyle} />
          )}
          {step.key === 'stage' && (
            <StepStage value={stage} onChange={setStage} />
          )}
          {step.key === 'taste' && (
            <StepTaste value={taste} onChange={setTaste} />
          )}
          {step.key === 'avoid' && (
            <StepAvoid value={avoid} onChange={setAvoid} />
          )}
          {step.key === 'shopStyle' && (
            <StepShopStyle value={shopStyle} onChange={setShopStyle} />
          )}
          {step.key === 'shop' && (
            <StepShopChoice value={shop} onChange={setShop} />
          )}
          {step.key === 'schedule' && (
            <StepSchedule value={schedule} onChange={setSchedule} />
          )}
        </motion.div>
      </AnimatePresence>
    </StepLayout>
  );
}